import '@mui/material/styles';
import { createTheme } from '@mui/material/styles';

// TypeScript module augmentation must be at the top before using the theme
declare module '@mui/material/styles' {
  interface Palette {
    chat: {
      colors: {
        aiPrimary: string;
        aiSecondary: string;
        aiGradientStart: string;
        aiGradientEnd: string;
        aiDark: string;
        aiLight: string;
        userBubble: string;
        userText: string;
        assistantText: string;
        chatBackground: string;
        chatBorder: string;
        messageBubbleBackground: string;
        messageBubbleBorder: string;
        timestampText: string;
        timestampTextLight: string;
        thinkingDot: string;
        streamingCursor: string;
        statusShadow: string;
      };
      gradients: {
        ai: string;
      };
    };
  }
  interface PaletteOptions {
    chat?: {
      colors?: {
        aiPrimary?: string;
        aiSecondary?: string;
        aiGradientStart?: string;
        aiGradientEnd?: string;
        aiDark?: string;
        aiLight?: string;
        userBubble?: string;
        userText?: string;
        assistantText?: string;
        chatBackground?: string;
        chatBorder?: string;
        messageBubbleBackground?: string;
        messageBubbleBorder?: string;
        timestampText?: string;
        timestampTextLight?: string;
        thinkingDot?: string;
        streamingCursor?: string;
        statusShadow?: string;
      };
      gradients?: {
        ai?: string;
      };
    };
  }
  interface BreakpointOverrides {
    xxl: true;
  }
  interface Theme {
    layout: {
      navHeight: {
        xs: number;
        sm: number;
      };
    };
  }
  interface ThemeOptions {
    layout?: {
      navHeight?: {
        xs?: number;
        sm?: number;
      };
    };
  }
}

//Colors object was created to that colors can be uniform for both inline styling and for the theme provider

export const Colors = {
  jotpsych_main: '#36229e', //Eggplant[700]
  jotpsych_dark: '#1f0862', // also button hovers
  brand_white: '#ffffff', // Pure White
  brand_black: '#000000', // Pure Black
  //Primary Palette is - Eggplant:
  primary: '#36229e', //Eggplant[700]
  primary900: '#2a2348',
  primary800: '#291d69',
  primary700: '#36229e',
  primary600: '#4d32d7',
  primary500: '#644ce0',
  primary400: '#836eed',
  primary300: '#ada1f3',
  primary200: '#e6e1ff',
  primary100: '#f5f4fc',
  //All greys below are eggplant-neutrals. ~Do not use regular greys!~
  grey900: '#030115',
  grey800: '#2a2533',
  grey700: '#4d4757',
  grey600: '#857f93',
  grey500: '#a39eae',
  grey400: '#bcbac9',
  grey300: '#d5d3e0',
  grey200: '#ebeaf1',
  grey100: '#f5f4f8',
  grey50: '#faf9fc',
  sucess: '',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  danger: '#eb4a26',
  dangerLight: '#faf6f5',
  body_bg: '',
  border: '',
  inverse: '',
  link: '#2196F3',
  linkHover: '#1976D2',
  eggplant900: '#2A2349',
  eggplant800: '#291D69',
  eggplant700: '#36229E',
  eggplant600: '#4D32D7',
  eggplant500: '#644CEO',
  eggplant400: '#836EED',
  eggplant300: '#AEA1F3',
  eggplant200: '#E6E1FF',
  eggplant100: '#F5F4FB',
  orange100: '#FFC79E',
  yellow200: '#FFDDB',
  eggplantNeutral900: '#040116',
  eggplantNeutral800: '#2A2533',
  eggplantNeutral700: '#4D4758',
  eggplantNeutral600: '#857F92',
  eggplantNeutral500: '#A39DAF',
  eggplantNeutral400: '#BCBACA',
  eggplantNeutral300: '#D5D3EO',
  eggplantNeutral200: '#EBEAFO',
  eggplantNeutral100: '#F5F4F8',
  eggplantNeutral50: '#FAF9FC',
};

// Chat UI specific colors using existing eggplant palette
export const ChatColors = {
  // AI/Assistant colors (purple gradient)
  aiPrimary: Colors.primary600, // '#4D32D7'
  aiSecondary: Colors.primary400, // '#836eed'
  aiGradientStart: Colors.primary400, // '#836eed'
  aiGradientEnd: Colors.primary600, // '#4D32D7'
  aiDark: Colors.primary700, // '#36229e'
  aiLight: Colors.primary300, // '#ada1f3'

  // User colors
  userBubble: Colors.primary600, // '#4D32D7'
  userBubbleHover: Colors.primary700, // '#36229e'

  // Background colors
  chatBackground: Colors.grey100, // '#f5f4f8'
  chatBorder: Colors.grey200, // '#ebeaf1'
  messageBubbleBorder: Colors.grey200, // '#ebeaf1'
  messageBubbleBackground: Colors.brand_white, // '#ffffff'

  // Text colors
  assistantText: Colors.grey800, // '#2a2533'
  userText: Colors.brand_white, // '#ffffff'
  timestampText: Colors.grey600, // '#857f93'
  timestampTextLight: Colors.grey500, // '#a39eae'

  // Status colors
  thinkingDot: Colors.primary500, // '#644CE0'
  streamingCursor: Colors.primary500, // '#644CE0'
  statusShadow: 'rgba(100, 76, 224, 0.4)',
};

/**
 * Creates an AI gradient for chat components
 * @param angle - Gradient angle in degrees (default: 135)
 */
export const createAIGradient = (angle = 135) =>
  `linear-gradient(${angle}deg, ${ChatColors.aiGradientStart} 0%, ${ChatColors.aiGradientEnd} 100%)`;

export const jotpsychTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
    },
  },
  layout: {
    navHeight: {
      xs: 52, // Mobile nav height
      sm: 64, // Desktop nav height (sm and up)
    },
  },
  palette: {
    primary: {
      main: Colors.jotpsych_main,
      dark: Colors.jotpsych_dark,
      // light: will be calculated from palette.primary.main,
      // contrastText: will be calculated to contrast with palette.primary.main
    },
    secondary: {
      main: '#E55A75', // rgb(229, 90, 117)
      dark: '#B7485E', // Darker for hover states (~20% darker)
    },
    grey: {
      50: Colors.grey50,
      100: Colors.grey100,
      200: Colors.grey200,
      300: Colors.grey300,
      400: Colors.grey400,
      500: Colors.grey500,
      600: Colors.grey600,
      700: Colors.grey700,
      800: Colors.grey800,
      900: Colors.grey900,
    },
    error: {
      main: Colors.danger,
    },
    success: {
      main: Colors.success,
    },
    warning: {
      main: Colors.warning,
    },
    info: {
      main: Colors.info,
    },
    customColorMain: {
      100: Colors.primary100,
      200: Colors.primary200,
      300: Colors.primary300,
      400: Colors.primary400,
      500: Colors.primary500,
      600: Colors.primary600,
      700: Colors.primary700,
      800: Colors.primary800,
      900: Colors.primary900,
    },
    backgroundColors: {
      primary: Colors.brand_white,
      secondary: Colors.primary100,
      tertiary: Colors.grey100,
      brand: Colors.primary700,
      warning: Colors.dangerLight,
      warningDark: Colors.danger,
    },
    borderColors: {
      primary: Colors.primary200,
      lightprimary: Colors.grey200,
      darkprimary: Colors.grey300,
      brand: Colors.primary700,
      warning: Colors.danger,
    },
    textColors: {
      lightHeader: Colors.primary900,
      darkHeader: Colors.grey900,
      accent: Colors.primary800,
      brand: Colors.primary700,
      brandContrast: Colors.brand_white,
      lightText: Colors.grey600,
      primaryText: Colors.grey700,
      darkText: Colors.grey900,
      warning: Colors.danger,
      disabled: Colors.grey500,
    },
    linkColors: {
      link: Colors.link,
      linkHover: Colors.linkHover,
    },
    newDesignColors: {
      eggplant900: Colors.eggplant900,
      eggplant800: Colors.eggplant800,
      eggplant700: Colors.eggplant700,
      eggplant600: Colors.eggplant600,
      eggplant500: Colors.eggplant500,
      eggplant400: Colors.eggplant400,
      eggplant300: Colors.eggplant300,
      eggplant200: Colors.eggplant200,
      eggplant100: Colors.eggplant100,
      orange100: Colors.orange100,
      yellow200: Colors.yellow200,
      eggplantNeutral900: Colors.eggplantNeutral900,
      eggplantNeutral800: Colors.eggplantNeutral800,
      eggplantNeutral700: Colors.eggplantNeutral700,
      eggplantNeutral600: Colors.eggplantNeutral600,
      eggplantNeutral500: Colors.eggplantNeutral500,
      eggplantNeutral400: Colors.eggplantNeutral400,
      eggplantNeutral300: Colors.eggplantNeutral300,
      eggplantNeutral200: Colors.eggplantNeutral200,
      eggplantNeutral100: Colors.eggplantNeutral100,
      eggplantNeutral50: Colors.eggplantNeutral50,
    },
    // @ts-expect-error - Module augmentation not being picked up by tsc, but works at runtime
    chat: {
      colors: ChatColors,
      gradients: {
        ai: createAIGradient(135),
      },
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTypography: {
      styleOverrides: {
        h4: {
          fontSize: '28px',
          fontWeight: 700,
          lineHeight: '33.6px',
        },
        h5: {
          fontSize: '22px',
          fontWeight: 700,
          lineHeight: '26.4px',
        },
        h6: {
          fontSize: '17px',
          fontWeight: 700,
          lineHeight: '25.5px',
        },
        subtitle1: {
          fontSize: '15px',
          fontWeight: 400,
          lineHeight: '22.5px',
        },
        body1: {
          fontSize: '17px',
          fontWeight: 400,
          lineHeight: '25.5px',
        },
        caption: {
          fontSize: '0.8rem',
          fontWeight: 300,
        },
      },
    },
  },
});
